import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  useTheme,
  // useScrollTrigger,
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Menu,
  MenuItem,
  Badge,
  Button,
  Avatar,
} from '@mui/material';

// import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import MoreIcon from '@mui/icons-material/MoreVert';
import MenuIcon from '@mui/icons-material/Menu';

import {
  useConfiguration,
  useConfigurationDispatch,
} from '../../providers/ConfigurationContext';
import { useApi } from '../../providers/ApiContext';
import {
  SET_DOC_TYPES,
  SET_MODEL_CONFIG,
  USER_FETCH,
} from '../../providers/actionTypes';
import { useUser, useUserDispatch } from '../../providers/UserContext';
import { getUser } from '../../actions/user';
import { useLocation } from 'react-router';
import { useAuth } from '../../providers/AuthContext';
import { startInteractiveLogin } from '@/util/sessionRecovery';
// import { RefreshSession } from '../RefreshSession';
import { MODEL_CONFIGURATIONS } from '../../models/modelConfigurations';
import { MODEL_FAMILIES } from '../../models/ModelFamilies';
import {
  useDocTypes,
  useDocTypesDispatch,
} from '../../providers/DocTypesContext';
import { getDocumentTypes } from '../../actions/documentType';

// function ElevationScroll(props) {
//   const { children, window } = props;
//   const trigger = useScrollTrigger({
//     disableHysteresis: true,
//     threshold: 15,
//     target: window ? window() : undefined,
//   });
//   return React.cloneElement(children, {
//     elevation: trigger ? 4 : 1,
//   });
// }

interface HeaderProps {
  mainSidebarOpen: boolean;
  toggleMainSidebar: () => void;
  colorInvert?: boolean;
  window?: () => Window | undefined;
}

const Header: React.FC<HeaderProps> = ({
  toggleMainSidebar,
  colorInvert = false,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const configuration = useConfiguration();
  const configuration_dispatch = useConfigurationDispatch();
  const api = useApi();
  const user = useUser();
  const userDispatch = useUserDispatch();
  const auth = useAuth();
  const doc_types = useDocTypes();
  const doc_types_dispatch = useDocTypesDispatch();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    useState<null | HTMLElement>(null);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if (
      auth.config &&
      !configuration?.model &&
      auth.config.llm_model_family &&
      auth.config.vllm_model_family
    ) {
      // Convert MODEL_FAMILY_TYPES to MODEL_FAMILIES (they have the same string values)
      const llmFamily = auth.config
        .llm_model_family as unknown as MODEL_FAMILIES;
      const vllmFamily = auth.config
        .vllm_model_family as unknown as MODEL_FAMILIES;

      if (
        llmFamily in MODEL_CONFIGURATIONS &&
        vllmFamily in MODEL_CONFIGURATIONS
      ) {
        configuration_dispatch({
          type: SET_MODEL_CONFIG,
          payload: {
            llm: MODEL_CONFIGURATIONS[llmFamily],
            vllm: MODEL_CONFIGURATIONS[vllmFamily],
          },
        });
      }
    }
  }, [auth.config, configuration?.model, configuration_dispatch]);

  useEffect(() => {
    if (auth.user && !user?.entity_id && api && auth.user_manager) {
      getUser(api)
        .then((result) => {
          userDispatch({ type: USER_FETCH, payload: result });
        })
        .catch((error) => {
          console.log('get user failed', error);
          if (error.response?.status === 404) {
            // authenticate should handle create user
            if (location.pathname !== '/authenticate') {
              navigate('/authenticate');
            }
          } else {
            if (auth.user_manager) {
              auth.user_manager.signoutRedirect();
            }
          }
        });
    }
  }, [
    auth.user,
    auth.user_manager,
    user,
    userDispatch,
    location.pathname,
    navigate,
    api,
  ]);

  useEffect(() => {
    if (api && user?.entity_id && doc_types === null) {
      getDocumentTypes(api)
        .then((result) => {
          doc_types_dispatch({ type: SET_DOC_TYPES, payload: result });
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [doc_types, doc_types_dispatch, user?.entity_id, api]);

  const { mode } = theme.palette;

  const logout = async () => {
    if (auth.user_manager) {
      await auth.user_manager.signoutRedirect();
    }
  };

  const menuId = 'primary-navbar-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem component={Link} to='/account'>
        <IconButton size='large' color='inherit'>
          <PersonOutlineOutlinedIcon />
        </IconButton>
        <p>Account</p>
      </MenuItem>
      <MenuItem
        onClick={() => {
          logout();
          handleMenuClose();
        }}
      >
        <IconButton size='large' color='inherit'>
          <LogoutOutlinedIcon />
        </IconButton>
        <p>Logout</p>
      </MenuItem>
    </Menu>
  );

  const mobileMenuId = 'primary-navbar-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      {/* <MenuItem>
        <IconButton size='large' color='inherit'>
          <Badge color='error'>
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <p>Notifications</p>
      </MenuItem> */}
      <MenuItem component={Link} to='/docs'>
        <IconButton size='large' color='inherit'>
          <HelpOutlineOutlinedIcon />
        </IconButton>
        <p>Documentation</p>
      </MenuItem>
      <MenuItem component={Link} to='/account'>
        <IconButton size='large' color='inherit'>
          <PersonOutlineOutlinedIcon />
        </IconButton>
        <p>Account</p>
      </MenuItem>
      <MenuItem
        onClick={() => {
          logout();
          handleMobileMenuClose();
        }}
      >
        <IconButton size='large' color='inherit'>
          <LogoutOutlinedIcon />
        </IconButton>
        <p>Logout</p>
      </MenuItem>
    </Menu>
  );

  return (
    <>
      {/* <ElevationScroll window={window}> */}
      <AppBar
        position={'sticky'}
        sx={{
          top: 0,
          display: 'flex',
        }}
      >
        <Toolbar sx={{ backgroundColor: theme.palette.secondary.main }}>
          <IconButton
            size='large'
            edge='start'
            color='primary'
            onClick={toggleMainSidebar}
            aria-label='menu'
            sx={{
              mr: 1,
            }}
          >
            <MenuIcon fontSize='large' />
          </IconButton>
          <Box
            component='a'
            href='/'
            title='AccQSure'
            width={175}
            sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}
          >
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
              width={1}
            >
              <Box
                component={'img'}
                src={
                  mode === 'light' && !colorInvert ? '/logo.png' : '/logo.png'
                }
                height={1}
                width={1}
              />
            </Box>
          </Box>
          <Box
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              justifyContent: 'center',
            }}
          >
            <Box component='a' href='/' title='AccQSure' width={175}>
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                alignItems={'center'}
                width={1}
              >
                <Box
                  component={'img'}
                  src={
                    mode === 'light' && !colorInvert ? '/logo.png' : '/logo.png'
                  }
                  height={1}
                  width={1}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1 }} />
          {user?.entity_id ? (
            <>
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                {/* <IconButton size='large' color='primary'>
                    <Badge color='error'>
                      <NotificationsIcon />
                    </Badge>
                  </IconButton> */}
                <IconButton
                  size='large'
                  color='primary'
                  href='/docs'
                  target='_blank'
                >
                  <Badge color='error'>
                    <HelpOutlineOutlinedIcon fontSize='large' />
                  </Badge>
                </IconButton>
                <IconButton
                  size='large'
                  edge='end'
                  aria-label='account of current user'
                  aria-controls={menuId}
                  aria-haspopup='true'
                  onClick={handleProfileMenuOpen}
                  // color='primary'
                >
                  <Avatar
                    // sx={{
                    //   bgcolor: theme.palette.primary.main,
                    // }}
                    alt={user?.first_name || ''}
                    src={'/bogus.jpg'}
                  />
                </IconButton>
              </Box>
              <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                  size='large'
                  aria-label='show more'
                  aria-controls={mobileMenuId}
                  aria-haspopup='true'
                  onClick={handleMobileMenuOpen}
                  color='primary'
                >
                  <MoreIcon fontSize='large' />
                </IconButton>
              </Box>
            </>
          ) : (
            <Button
              onClick={() => {
                if (auth.user_manager) {
                  void startInteractiveLogin(auth.user_manager);
                }
              }}
              variant='contained'
              color='primary'
              size='large'
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>
      {/* </ElevationScroll> */}
      {renderMobileMenu}
      {renderMenu}
      {/* {auth.user ? <RefreshSession /> : undefined} */}
    </>
  );
};

export default Header;
